import React, { useEffect, useState } from 'react';
import {
	Alert,
	Button,
	Tooltip,
	Stack,
	Typography,
	Grid,
	TextField,
	Autocomplete,
	Menu,
	MenuItem,
} from '@mui/material';
import ControlCameraIcon from '@mui/icons-material/ControlCamera';
import EngineeringIcon from '@mui/icons-material/Engineering';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ListIcon from '@mui/icons-material/List';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import DateFnsUtils from '@date-io/date-fns';
import { DataGrid } from '@mui/x-data-grid';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import moment from 'moment';
import { timestampToNormalDateTime } from '../../helpers/hours';
import { capLetters, getFullName, handleError } from '../../helpers/utils';
import { LoadingButton } from '@mui/lab';
import { getIncidents } from '../../actions/incidents';
import { DIVISIONS, VIEW_OPTIONS } from '../utils/lists';
import { getDataByUrl } from '../../actions/catalogs';
import { generateSimpleExcel } from '../../actions/excel';
import { JustIconStyle } from '../orderTracking/OrderHeader';

// Filter options
const FILTER_OPTIONS = ['División', 'Asesor', 'Todo'];

// Results table
export const SummaryResultsIncidents = () => {
	// Form states
	const [date1, setDate1] = useState(null);
	const [date2, setDate2] = useState(null);
	const [division, setDivision] = useState(null);
	const [manager, setManager] = useState(null);
	const [managers, setManagers] = useState([]);
	const [searchOption, setSearchOption] = useState(null);
	// const [listFilter, setListFilter] = useState('');

	// Page size
	const [pageSize, setPageSize] = useState(20);

	// Total biases
	const [resultsTotal, setResultsTotal] = useState(-1);

	// Is loading
	const [isLoading, setIsLoading] = useState(false);

	// Is loading
	const [viewOption, setViewOption] = useState('Todo');

	// Rows state
	const [rows, setRows] = useState(null);
	const [rows2, setRows2] = useState(null);

	// Generate rows
	const generateRows = async (formattedDate1, formattedDate2) => {
		setIsLoading(true);
		// Get division code
		const codeDivision =
			DIVISIONS.find((div) => div.label === division)?.name || null;

		// Get manager id
		const managerId =
			managers.find((man) => man.name === manager)?.id || null;

		// Get data from API
		const data = await getIncidents(
			formattedDate1,
			formattedDate2,
			searchOption,
			codeDivision,
			managerId
		);

		// If results are empty set an empty array
		if (data?.length === 0 || !data) {
			// Set rows
			setRows([]);
			setRows2([]);
			// Set results total
			setResultsTotal(0);
		} else {
			// Set results total
			setResultsTotal(data.length);
			// Map data results
			const res = data?.map((item) => {
				let newMessage = item.message;
				if (item.message.length >= 30) {
					newMessage = item.message
						.replace('Consulte al supervisor de área.', '')
						.split(':');
					newMessage = capLetters(
						newMessage.slice(1, newMessage.length).join(' ')
					);
				}
				return {
					...item,
					solved_date: timestampToNormalDateTime(item.solved_date),
					message: newMessage,
					created: timestampToNormalDateTime(item.created),
				};
			});

			// Set mapped rows
			setRows(res);
			setRows2(res);
		}

		setIsLoading(false);
	};

	// Generate excel
	const handleGenerateExcel = async () => {
		// Format dates
		const formattedDate1 = moment(date1).format('DD_MM_YYYY');
		const formattedDate2 = moment(date2).format('DD_MM_YYYY');

		// Generate rows
		const newRows = rows2.map((item) => ({
			División: item?.division,
			Pedido: item?.order?.number_order,
			'Reportado por': item.user.name,
			'Creado por': item?.order?.created_by?.name || 'No asignado',
			Incidencia: item?.message,
			Creado: item?.created,
			'Fecha de resolución': item?.solved_date || '-',
		}));

		// Generate excel
		await generateSimpleExcel(
			newRows,
			`Rechazos_Periodo_${formattedDate1}__${formattedDate2}`
		);
	};

	useEffect(() => {
		const handleGetManagers = () => {
			const managerUserTypeId = 5;
			getDataByUrl(`/api/auth/users?search=${managerUserTypeId}`)
				.then(({ data }) => {
					if (data.count > 0) {
						setManagers(
							data.results.map((user) => ({
								name: getFullName(user),
								id: user.id,
							}))
						);
					} else {
						setManagers([]);
					}
				})
				.catch((error) =>
					handleError(
						error,
						'Ocurrió un error al intentar traer a los asesores'
					)
				);
		};
		handleGetManagers();
	}, []);

	useEffect(() => {
		if (rows?.length) {
			switch (viewOption) {
				case 'Mesa de control':
					setRows2(
						rows.filter(
							(item) =>
								item?.user?.area?.name === 'Mesa de Control'
						)
					);
					break;
				case 'Administradores':
					setRows2(
						rows.filter((item) => item?.user?.user_type?.id === 2)
					);
					break;
				case 'Asesores':
					setRows2(
						rows.filter((item) => item?.user?.user_type?.id === 5)
					);
					break;
				case 'Operadores':
					setRows2(
						rows.filter(
							(item) =>
								(item?.user?.area?.name !== 'Mesa de Control' ||
									item?.user?.area?.id !== 1) &&
								[3, 4].includes(item?.user?.user_type?.id)
						)
					);
					break;
				default:
					setRows2(rows);
					break;
			}
		}
	}, [viewOption]);

	// Handle get results
	const handleGetResults = () => {
		// Date format to YYYY-MM-DD
		const formattedDate1 = moment(date1).format('YYYY-MM-DD');
		const formattedDate2 = moment(date2).add(1, 'day').format('YYYY-MM-DD');
		// Generate rows from formatted date
		generateRows(formattedDate1, formattedDate2);
	};

	// Handle Reset
	const handleReset = () => {
		setViewOption('Todo');
		setDate1(null);
		setDate2(null);
		setDivision(null);
		setManager(null);
		setSearchOption(null);
		setRows([]);
		setRows2([]);
		setResultsTotal(-1);
	};

	// Columns
	const columns = [
		{
			field: 'division',
			headerName: 'División',
			...columnStyleCell,
			width: 100,
			renderCell: ({ row }) => (
				<div style={rowDialogStyle}>
					<span className='table-cell-trucate'>{row?.division}</span>
				</div>
			),
		},
		{
			field: 'order.number_order',
			headerName: 'Pedido',
			...columnStyleCell,
			width: 100,
			renderCell: ({ row }) => (
				<div style={rowDialogStyle}>
					<span className='table-cell-trucate'>
						{row?.order.number_order}
					</span>
				</div>
			),
		},
		{
			field: 'user.name',
			headerName: 'Reportado por',
			...columnStyleCell,
			width: 250,
			renderCell: ({ row }) => (
				<Tooltip title={row?.user?.area?.name}>
					<div style={rowDialogStyle}>
						<span className='table-cell-trucate'>
							{row?.user?.name}
						</span>
					</div>
				</Tooltip>
			),
		},
		{
			field: 'created',
			headerName: 'Fecha de creación',
			...columnStyleCell,
			width: 200,
			renderCell: ({ row }) => (
				<div style={rowDialogStyle}>
					<span className='table-cell-trucate'>{row?.created}</span>
				</div>
			),
		},
		{
			field: 'order.created_by',
			headerName: 'Creado por',
			...columnStyleCell,
			width: 250,
			renderCell: ({ row }) => (
				<Tooltip title='Creador del pedido'>
					<div style={rowDialogStyle}>
						<span className='table-cell-trucate'>
							{row?.order?.created_by?.name || 'No asignado'}
						</span>
					</div>
				</Tooltip>
			),
		},
		{
			field: 'solved_date',
			headerName: 'Fecha de resolución',
			...columnStyleCell,
			width: 250,
			renderCell: ({ row }) => (
				<div style={rowDialogStyle}>
					<span className='table-cell-trucate'>
						{row?.solved_date || '-'}
					</span>
				</div>
			),
		},
		{
			field: 'message',
			headerName: 'Incidencia',
			...columnStyleCell,
			width: 460,
			renderCell: ({ row }) => (
				<div
					style={{
						...rowDialogStyle,
						textWrap: 'initial',
						height: '100px',
					}}
				>
					<p>{row?.message}</p>
				</div>
			),
		},
	];

	return (
		<div style={divStyle}>
			<Stack
				direction='row'
				alignItems='flex-end'
				justifyContent='space-between'
				style={stackStyle}
			>
				<Typography variant='h4' gutterBottom>
					Resumen de rechazos
				</Typography>
				<LocalizationProvider
					dateAdapter={AdapterDateFns}
					adapterLocale={es}
					utils={DateFnsUtils}
				>
					<Grid container spacing={2} style={gridStyle}>
						<Grid item xs={3}>
							{/* Date 1 */}
							<DesktopDatePicker
								style={{ width: '30%' }}
								label='Desde'
								inputFormat='dd/MM/yyyy'
								value={date1}
								disableFuture
								onChange={(val) => {
									setDate1(val);
								}}
								renderInput={(params) =>
									SelectComponent(params, 'Desde')
								}
							/>
						</Grid>
						<Grid item xs={3}>
							{/* Date 2 */}
							<DesktopDatePicker
								style={{ width: '30%' }}
								label='Desde'
								inputFormat='dd/MM/yyyy'
								value={date2}
								disableFuture
								onChange={(val) => {
									setDate2(val);
								}}
								renderInput={(params) =>
									SelectComponent(params, 'Hasta')
								}
							/>
						</Grid>
						<Grid item xs={3}>
							<Autocomplete
								required
								fullWidth
								value={searchOption}
								onChange={(_, value) => setSearchOption(value)}
								options={FILTER_OPTIONS}
								renderInput={(params) =>
									TextFieldComponent(params, 'Buscar por')
								}
							/>
						</Grid>
						{searchOption === 'División' && (
							<Grid item xs={3}>
								{/* Division */}
								<Autocomplete
									required
									fullWidth
									value={division}
									onChange={(_, value) => setDivision(value)}
									options={DIVISIONS.map(
										(item) => item.label
									)}
									renderInput={(params) =>
										SelectComponent(params, 'División')
									}
								/>
							</Grid>
						)}

						{managers.length > 0 && searchOption === 'Asesor' && (
							<Grid item xs={3}>
								{/* Division */}
								<Autocomplete
									required
									fullWidth
									value={manager}
									onChange={(_, value) => setManager(value)}
									options={managers.map((item) => item.name)}
									renderInput={(params) =>
										SelectComponent(params, 'Asesor')
									}
								/>
							</Grid>
						)}

						{(rows?.length > 0 || rows2?.length > 0) && (
							<Grid item xs={3}>
								{/* View option */}
								<Autocomplete
									required
									fullWidth
									value={viewOption}
									onChange={(_, value) =>
										setViewOption(value)
									}
									options={VIEW_OPTIONS}
									renderInput={(params) =>
										SelectComponent(params, 'Solo ver')
									}
								/>
							</Grid>
						)}

						<Grid item xs={8} style={gridButtonStyle}>
							<Button
								variant='contained'
								color='error'
								disabled={!(date1 !== null && date2 !== null)}
								onClick={handleReset}
								style={{ margin: '0 15px 0 0' }}
							>
								Limpiar
							</Button>
							<Button
								variant='contained'
								color='success'
								disabled={!(rows2?.length > 0)}
								onClick={handleGenerateExcel}
								style={{ margin: '0 15px 0 0' }}
							>
								Exportar
							</Button>
							<LoadingButton
								variant='contained'
								color='primary'
								onClick={handleGetResults}
								loading={isLoading}
								style={{ margin: '0 15px 0 0' }}
								disabled={
									date1 === null ||
									date2 === null ||
									(searchOption === 'División' &&
										division === null) ||
									(searchOption === 'Asesor' &&
										manager === null) ||
									searchOption === null
								}
							>
								Obtener
							</LoadingButton>
						</Grid>
					</Grid>
				</LocalizationProvider>
			</Stack>
			<hr />

			{resultsTotal >= 0 && (
				<Typography variant='h6'>
					Total de partidas: {resultsTotal} | Vista:{' '}
					{searchOption?.length ? searchOption : 'Todo'}
				</Typography>
			)}

			<div style={datagridTableStyle}>
				{/* Datagrid table */}
				{rows2?.length > 0 ? (
					<DataGrid
						style={{ height: '600px', margin: '10px 0 0 0' }}
						loading={isLoading}
						rowsPerPageOptions={[20, 30, 50, 70, 100]}
						pageSize={pageSize}
						onPageSizeChange={(newPageSize) =>
							setPageSize(newPageSize)
						}
						density='comfortable'
						rows={rows2}
						getRowHeight={() => 'auto'}
						columns={columns}
					/>
				) : (
					<>
						{rows2 === null ? (
							<Alert severity='success'>
								Busca todos las rechazos dentro de un periodo de
								tiempo
							</Alert>
						) : (
							<Alert severity='info'>
								No hay ninguna incidencia
							</Alert>
						)}
					</>
				)}
			</div>
		</div>
	);
};

const SelectComponent = (params, label) => (
	<TextField fullWidth required margin='normal' {...params} label={label} />
);

const rowDialogStyle = {
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	height: '150px',
	justifyContent: 'space-between',
	margin: '0 auto',
};

const stackStyle = {
	padding: '20px 20px 0 0',
	margin: '0 0 40px 0',
	width: '100%',
};

const divStyle = {
	padding: '20px',
};

const gridStyle = {
	width: '70%',
	display: 'flex',
	justifyContent: 'flex-end',
};

const gridButtonStyle = {
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
};

const datagridTableStyle = { margin: '20px 0' };

const columnStyleCell = {
	headerClassName: 'column-header-pilu',
	headerAlign: 'center',
};

const TextFieldComponent = (params, label) => (
	<TextField fullWidth required margin='normal' {...params} label={label} />
);
